import type { ReactNode } from "react"
import {
	IconCheckOutline18 as Check,
	IconChevronDownOutline18 as ChevronDown,
} from "nucleo-ui-outline-18"

import { Button, DropdownMenu, DropdownMenuItem } from "@productlane/ui"

export interface SelectOption<T extends string> {
	value: T
	label: string
	/** A second line under the label, for what picking this does. */
	hint?: string
	/** Still listed, so the row can say why it is refused instead of vanishing. */
	unavailable?: boolean
}

interface ItemsProps<T extends string> {
	value: T
	options: readonly SelectOption<T>[]
	onSelect: (value: T) => void
	/** Runs instead of onSelect for an unavailable row, to say why. */
	onUnavailable?: (option: SelectOption<T>) => void
	itemClassName?: string
}

/** The rows alone, for a picker whose trigger is not a button. */
export function OptionItems<T extends string>({
	value,
	options,
	onSelect,
	onUnavailable,
	itemClassName,
}: ItemsProps<T>) {
	return (
		<>
			{options.map((option) => (
				<DropdownMenuItem
					key={option.value}
					IconRight={option.value === value ? Check : undefined}
					className={
						option.unavailable ? "opacity-50" : (itemClassName ?? undefined)
					}
					onSelect={(event) => {
						if (option.unavailable) {
							event.preventDefault()
							onUnavailable?.(option)
							return
						}
						onSelect(option.value)
					}}
				>
					{option.hint ? (
						<span className="flex min-w-0 flex-col">
							<span>{option.label}</span>
							<span className="truncate text-xs font-normal text-tertiary">
								{option.hint}
							</span>
						</span>
					) : (
						option.label
					)}
				</DropdownMenuItem>
			))}
		</>
	)
}

interface Props<T extends string> extends ItemsProps<T> {
	/** Trigger text, when the active option's label is not what it should read. */
	label?: ReactNode
	align?: "start" | "end"
	size?: "xs" | "sm"
	disabled?: boolean
	/** A min-width, so the trigger keeps its box when the label changes. */
	triggerClassName?: string
	testId?: string
}

/** Every "pick one, the active one is checked" menu in the policy editor. */
export function OptionSelect<T extends string>({
	label,
	align = "start",
	size,
	disabled,
	triggerClassName,
	testId,
	...items
}: Props<T>) {
	const active = items.options.find((option) => option.value === items.value)

	return (
		<DropdownMenu
			contentProps={{ align }}
			trigger={
				<Button
					variant="outline"
					iconRight={ChevronDown}
					size={size}
					disabled={disabled}
					className={triggerClassName}
					wrapperClassName={
						triggerClassName ? "w-full justify-between" : undefined
					}
					data-testid={testId}
				>
					{label ?? active?.label ?? items.value}
				</Button>
			}
		>
			<OptionItems {...items} />
		</DropdownMenu>
	)
}
